import { ReactNode } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { XCircle } from '@phosphor-icons/react';
import { useAuthStore } from '../../../store/storeAuth';

export interface TextContentTab {
  id: string;
  label: string;
  icon: ReactNode;
  hint?: string;
}

interface TextContentModalProps {
  open: boolean;
  onClose: () => void;
  activeTab: string;
  onTabChange: (id: string) => void;
  tabs: TextContentTab[];
  children: (activeTab: string) => ReactNode;
}

export default function TextContentModal({
  open,
  onClose,
  activeTab,
  onTabChange,
  tabs,
  children,
}: TextContentModalProps) {
  const user = useAuthStore(state => state.user);

  if (!open) return null;

  const isDark = user?.theme === 'dark';
  const borderColor = isDark ? '#475467' : '#EAECF0';
  const textColor = isDark ? '#D0D5DD' : '#101828';
  const subTextColor = isDark ? '#98A2B3' : '#667085';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
          bgcolor: isDark ? '#1D2939' : '#FFF',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', height: '100%' }}>
        <Box
          sx={{
            width: '220px',
            borderRight: `1px solid ${borderColor}`,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              padding: '16px',
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            <Typography fontSize="16px" fontWeight={600} color={textColor}>
              Text Content
            </Typography>
            <Typography fontSize="12px" color={subTextColor}>
              Select a field to view or edit
            </Typography>
          </Box>
          <List sx={{ padding: '8px', flex: 1, overflowY: 'auto' }}>
            {tabs.map(tab => {
              const selected = tab.id === activeTab;
              return (
                <ListItemButton
                  key={tab.id}
                  selected={selected}
                  onClick={() => onTabChange(tab.id)}
                  sx={{
                    borderRadius: '8px',
                    marginBottom: '4px',
                    color: textColor,
                    '&.Mui-selected': {
                      bgcolor: isDark ? '#344054' : '#F2F4F7',
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: isDark ? '#475467' : '#EAECF0',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '32px', color: textColor }}>
                    {tab.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={tab.label}
                    secondary={tab.hint}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: selected ? 600 : 400,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '11px',
                      color: subTextColor,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            <Typography fontSize="14px" fontWeight={600} color={textColor}>
              {tabs.find(t => t.id === activeTab)?.label || ''}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <XCircle color="#667085" />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              padding: '20px',
              '& > *': {
                width: '100%',
                maxWidth: '100%',
              },
              '& textarea': {
                width: '100% !important',
                maxWidth: '100% !important',
                minWidth: 0,
                boxSizing: 'border-box',
              },
              '& div[style*="width: 360px"]': {
                width: '100% !important',
                maxWidth: '100% !important',
              },
              '& div[style*="width: 360px"] .MuiBox-root': {
                width: '100% !important',
                maxWidth: '100% !important',
                boxSizing: 'border-box',
              },
            }}
            className="nodrag"
          >
            {children(activeTab)}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
